/*
 * @Description:
 * @Author: Jack Deng
 * @Date: 2021-04-08 16:12:43
 * @LastEditors: Jack Deng
 * @LastEditTime: 2022-06-08 12:19:27
 * @FilePath: /unifyxp-v2-store-portal/src/pages/SalesChannelProductsConfig/SalesChannelProductsLinkProduct/util.ts
 */
import { getI18nFromGeneral } from '@/src/tools/tools';
import { get, isNumber } from 'lodash';
import { accMul } from 'src/tools/math';

type Price = {
  productUuid: string;
  listPriceUuid: string;
  rrp: number;
};

export const translatePriceTier = (
  listPrices: Array<any> = [],
  localPrices: Array<any> = [],
  price: number,
  currencyInfo: any,
  currency: string,
  {
    getI18n,
    isGiftCard,
    salesChannelCurrency,
    defaultCurrency,
    decimalPosition,
  },
) => {
  // console.log('listPrices', listPrices);
  // console.log('price', price);
  const localPrice =
    (localPrices && localPrices[salesChannelCurrency]?.salePrice) || 0;
  const defalutPriceIsLocal = localPrice > 0;
  const defaultListPrice = localPrice || price;
  const defaultListPriceCurrency = localPrice ? salesChannelCurrency : currency;
  const exchangeRate = get(currencyInfo, 'exchangeRate');
  const _handleOnExchangeRate = (p) => {
    return accMul(p || 0, exchangeRate || 1);
  };
  const list =
    isGiftCard && salesChannelCurrency === defaultCurrency
      ? [
          {
            label: `${defaultCurrency} ${defaultListPrice} (${getI18n(
              'salesChannelProductDetail.index.defaultlistprice',
            )})`,
            value: '1',
          },
        ]
      : [
          ...listPrices.map((l) => ({ ...l, uuid: l.priceTierUuid })),
          {
            uuid: '1',
            price: defaultListPrice,
            title: getI18n('salesChannelProductDetail.index.defaultlistprice'),
            currencyCode: defaultListPriceCurrency,
          },
        ]
          .filter(
            (l) =>
              l.currencyCode === currency &&
              (l.price || l.price === 0) &&
              l.price !== '-',
          )

          .map((l) => {
            const uuid = l.uuid;
            const isDefault = uuid == '1';
            const res = defalutPriceIsLocal
              ? l.price
              : _handleOnExchangeRate(l.price);

            const price =
              isNumber(decimalPosition) && isNumber(res)
                ? res.toFixed(decimalPosition)
                : res;
            const nonDefaultPrice =
              isNumber(decimalPosition) && isNumber(l.price)
                ? l.price.toFixed(decimalPosition)
                : l.price;
            return {
              label: `${currency} ${isDefault ? price : nonDefaultPrice} (${
                l.title
              })`,
              value: l.uuid,
            };
          });
  return list;
};

/**
 * 计算产品的RRP值，rrp跟各梯度价格一一对应，有就展示无就不需展示：
 * 梯度1. 选中的price tier的RRP
 * 梯度2. localPrices中primaryCurrency的RRP
 * 梯度3. 产品的defaultRrp （sc币种不是reporting时，不展示）
 */
export const calculateRrp = (
  product: any,
  listPriceUuid: string,
  primaryCurrencyCode?: string,
  differentCurrency?: boolean,
) => {
  let finalRrp = null;
  let rrpSet = false;

  // 1. 价格梯度的RRP（优先级最高）
  if (listPriceUuid !== '1') {
    const selectedPriceTier = product.listPrices?.find(
      (p) => p.priceTierUuid === listPriceUuid,
    );
    finalRrp = selectedPriceTier?.rrp || null;
    rrpSet = true;
  }

  // 2. localPrices中primaryCurrency的RRP
  if (
    !rrpSet &&
    primaryCurrencyCode &&
    product.localPrices &&
    product.localPrices[primaryCurrencyCode]?.salePrice
  ) {
    finalRrp = product.localPrices[primaryCurrencyCode].rrp || null;
    rrpSet = true;
  }

  // 3. 使用产品的默认RRP值（sc币种不是reporting时，不展示）
  if (!rrpSet) {
    finalRrp = !differentCurrency ? product?.defaultRrp : null;
    rrpSet = true;
  }

  return finalRrp;
};

/**
 * 为产品分配价格和RRP值，支持多货币展示
 *
 * 价格优先级：
 * 1. price tier选中的价格
 * 2. localPrices中primaryCurrency的价格
 * 3. 产品的defaultSalePrice
 */
export const assignPriceToProduct = (
  prices: Array<Price>,
  products: Array<any>,
  primaryCurrencyCode?: string,
  differentCurrency?: boolean,
) => {
  const result = products.map((product) => {
    const price = prices.find((price) => price.productUuid === product.uuid);
    // 处理listPrice逻辑
    const listPriceUuid = price?.listPriceUuid || '1';

    // 处理price值，优先顺序：price tier > localPrices > defaultSalePrice
    const finalPrice =
      findPrice(listPriceUuid, product.listPrices) ||
      (primaryCurrencyCode &&
        product.localPrices &&
        product.localPrices[primaryCurrencyCode]?.price) ||
      product.defaultSalePrice;

    // 计算RRP值
    const finalRrp = calculateRrp(
      product,
      listPriceUuid,
      primaryCurrencyCode,
      differentCurrency,
    );

    return {
      ...product,
      rrp: finalRrp,
      listPriceUuid,
      price: finalPrice,
    };
  });
  return result;
};

export const assignVariantToProduct = (
  variants: Array<any>,
  products: Array<any>,
) => {
  const result = products.map((product) => {
    const variant = variants.find(
      (variant) => variant.productUuid === product.uuid,
    );
    return {
      ...product,
      variant: variant?.values || [],
    };
  });
  return result;
};

export const assignProductServiceToProduct = (
  productService: any,
  products: Array<any>,
) => {
  if (
    products.length === 1 &&
    (products[0].productType === 'GiftCard' ||
      products[0].productType === 'Service')
  ) {
    return products.map((product) => ({
      ...product,
      productService: productService ? productService : { serviceDetails: [] },
    }));
  } else {
    return products;
  }
};

export const validateLabel = (index, option, options) => {
  const label = option.label;

  const duplicateError = !options
    .filter((_, i) => index !== i)
    .every((o) => o.label !== label);
  const emptyFieldError = !option.label;
  return {
    ...option,
    labelError: duplicateError || emptyFieldError,
    labelHelp: emptyFieldError
      ? getI18nFromGeneral('general.error.optionlabelcannotbeempty')
      : duplicateError
      ? `${getI18nFromGeneral('general.error.youhavealreadyusedthelabel')} "${
          option.label
        }"`
      : '',
  };
};

export const validateOptions = (options, products) => {
  let validate = true;
  //validate empty field
  const result = options.map((option) => {
    let err1 = {};
    let err2 = {};
    if (!option.label) {
      validate = false;
      err1 = {
        labelError: true,
        labelHelp: getI18nFromGeneral('general.error.optionlabelcannotbeempty'),
      };
    }
    if (
      !option.values ||
      (Array.isArray(option.values) && option.values.length === 0)
    ) {
      validate = false;
      err2 = {
        valuesError: true,
        valuesHelp: getI18nFromGeneral(
          'general.error.optionvaluecannotbeempty',
        ),
      };
    }
    return {
      ...option,
      ...err1,
      ...err2,
    };
  });

  //validate variants
  const list = products.map((product) => {
    if (product.variant && Array.isArray(product.variant)) {
      const variants = product.variant.map((v) => {
        if (!v?.value) {
          validate = false;
          return {
            ...v,
            err: getI18nFromGeneral('general.error.variantvaluecannotbeempty'),
          };
        } else {
          return v;
        }
      });
      return {
        ...product,
        variant: variants,
      };
    } else {
      return product;
    }
  });
  return {
    validate,
    options: result,
    products: list,
  };
};

const findPrice = (id: string, list: Array<any>) => {
  return id ? list.find((i) => i.priceTierUuid === id)?.price : null; //原来是哦、uuid？
};

export const getLinkProductType = (type: StoreProductType) => {
  switch (type) {
    case StoreProductType.Simple: {
      return LinkProductType.Single;
    }
    case StoreProductType.Set: {
      return LinkProductType.Multiple;
    }
    default: {
      return type;
    }
  }
};

export enum LinkProductType {
  Single = 'Single',
  Multiple = 'Multiple',
  ServiceCard = 'ServiceCard',
  GiftCard = 'GiftCard',
}

export enum StoreProductType {
  Simple = 'Simple',
  Set = 'Set',
  ServiceCard = 'ServiceCard',
  GiftCard = 'GiftCard',
}
